import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  ModalBottomButtons,
  Select,
  type SelectOptionType,
} from '@bp/ui-components';
import { graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { Form, Formik } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { useMutation, useQuery } from 'urql';
import { ensureDate } from 'utils/dateCalculations';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import { CreateBigBlueButtonsMutationDocument } from './BigBlueButtonForm';
import { validationSchema } from './bildungsplattformForm.schema';

const CreateBildungsplattformsMutationDocument = graphql(`
  mutation CreateBildungsplattforms($input: [BildungsplattformCreateInput!]!) {
    createBildungsplattforms(input: $input) {
      bildungsplattforms {
        uuid
        __typename
      }
    }
  }
`);

const UpdateBildungsplattformsMutationDocument = graphql(`
  mutation UpdateBildungsplattformsInBildungsplattformForm(
    $update: BildungsplattformUpdateInput!
    $where: BildungsplattformWhere!
  ) {
    updateBildungsplattforms(update: $update, where: $where) {
      bildungsplattforms {
        uuid
        __typename
        allowStudentDirectMessages
        organizationConnection {
          __typename
          edges {
            __typename
            properties {
              __typename
              version
              notAfter
              notBefore
            }
          }
        }
      }
    }
  }
`);

const SettingsBildungsplattformFormQueryDocument = graphql(`
  query SettingsBildungsplattformFormData($uuid: ID!) {
    bildungsplattforms(where: { uuid: $uuid }) {
      uuid
      __typename
      allowStudentDirectMessages
      allowParentExcuses
      currentSchoolyear
      organizationConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            notAfter
            notBefore
            version
            contractRef
          }
        }
      }
    }
  }
`);

type BildungsplattformValuesType = {
  allowStudentDirectMessages: boolean;
  notAfter?: string;
  notBefore: string;
  version: string;
};

type BildungsplattformFormProps = { onClose: () => void };

export const BildungsplattformForm = ({ onClose }: BildungsplattformFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { organizationProducts, currentSchoolYear, bigBlueButtonLicense } = useContext(OrganizationConfigContext);

  const bpUuid = organizationProducts?.find((d) => d.__typename === 'Bildungsplattform')?.uuid;

  const [{ data: queryData }] = useQuery({
    query: SettingsBildungsplattformFormQueryDocument,
    variables: {
      uuid: bpUuid ?? '',
    },
  });

  const permissions = usePermissionChecker();

  const currentBooking = queryData?.bildungsplattforms[0];
  const currentBookingEdge = currentBooking?.organizationConnection.edges[0];

  const [, updateBildungsplattform] = useMutation(UpdateBildungsplattformsMutationDocument);
  const [, createBildungsplattform] = useMutation(CreateBildungsplattformsMutationDocument);
  const [, createBBBs] = useMutation(CreateBigBlueButtonsMutationDocument);

  const initialValues: BildungsplattformValuesType = {
    version: currentBookingEdge?.properties.version ?? '',
    allowStudentDirectMessages: currentBooking?.allowStudentDirectMessages ?? false,
    notBefore: ensureDate(currentBookingEdge?.properties.notBefore ?? currentSchoolYear.start ?? null).toUTCString(),
    notAfter: currentBookingEdge?.properties.notAfter
      ? ensureDate(currentBookingEdge?.properties.notAfter).toUTCString()
      : undefined,
  };

  const onSubmit = async (values: BildungsplattformValuesType) => {
    if (permissions?.canUpdateBildungsplattform({ uuid: organizationUuid })) {
      // wenn pro gebucht wird, sind 50 bbb inklusive...
      if (values.version === 'pro' && !bigBlueButtonLicense) {
        await createBBBs({
          input: [
            {
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notBefore: values.notBefore,
                    notAfter: values.notAfter ?? null,
                    version: '50',
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });
      }

      if (bpUuid === undefined) {
        const resp = await createBildungsplattform({
          input: [
            {
              allowStudentDirectMessages: values.allowStudentDirectMessages,
              organization: {
                connect: {
                  overwrite: true,
                  edge: {
                    notBefore: values.notBefore,
                    notAfter: values.notAfter ?? null,
                    version: values.version,
                  },
                  where: { node: { uuid: organizationUuid } },
                },
              },
            },
          ],
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      } else {
        const resp = await updateBildungsplattform({
          update: {
            allowStudentDirectMessages: values.allowStudentDirectMessages,
            organization: {
              update: {
                edge: {
                  notBefore: values.notBefore,
                  notAfter: values.notAfter ?? null,
                  version: values.version,
                },
              },
            },
          },
          where: { uuid: bpUuid },
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          showSuccessToast(t('common.saved'));
          onClose();
        }
      }
    }
  };

  const versionOptions: SelectOptionType[] = [
    {
      label: t('app.pro'),
      value: 'pro',
    },
    {
      label: t('app.basic'),
      value: 'basic',
    },
    {
      label: t('app.community'),
      value: 'community',
    },
  ];

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ resetForm, setFieldValue, handleChange, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={8}>
                  <GridRow>
                    <GridColumn width={4}>
                      <Select
                        options={versionOptions}
                        value={versionOptions.find((o) => o.value === values.version)}
                        onChange={(event) => {
                          const option: SelectOptionType | null = event as SingleValue<SelectOptionType>;
                          setFieldValue('version', option?.value);
                        }}
                        name={'version'}
                        label={t('common.version')}
                      />
                    </GridColumn>
                    <GridColumn width={4}>
                      <DatePicker
                        label={t('institution.bookedFromDate')}
                        onChange={(e) => {
                          setFieldValue('notBefore', e?.toUTCString());
                        }}
                        value={ensureDate(values.notBefore)}
                        name={'notBefore'}
                        showMonthYearDropdown
                      />
                    </GridColumn>
                    <GridColumn width={4}>
                      <DatePicker
                        label={t('institution.bookedUntilDate')}
                        onChange={async (e) => {
                          await setFieldValue('notAfter', e?.toUTCString());
                        }}
                        value={values.notAfter ? ensureDate(values.notAfter) : undefined}
                        name={'notAfter'}
                        showMonthYearDropdown
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
                <GridColumn width={4} align='end'>
                  <Checkbox
                    className='mt-3'
                    label={t('institution.allowStudentDirectMessages')}
                    name={'allowStudentDirectMessages'}
                    onChange={handleChange}
                    checked={values.allowStudentDirectMessages ?? false}
                    error={errors.allowStudentDirectMessages}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              errors={errors}
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
